// The selected pin and a tapped group's pins popping out (docs/SPEC.md item 4 "Tapping pins"; web/map.js
// updateSelectedPin, clusterClick). The map's symbols can't animate one at a time, so these are HTML markers with the
// very same image (the shared pin look), left out of the map's own layers meanwhile (MapController.drawnElsewhere).
// The selected pin grows to pins.tap.selectScale with a short spring and shows its price label; deselected, it shrinks
// back. A tapped group centres, zooms in a step and its pins pop out to their places over pins.tap.popMs.
import * as maplibregl from "maplibre-gl";
import type * as GeoJSON from "geojson";
import type { FeedEvent } from "../data/event";
import { SHARED, T } from "../shared/config";
import { RULES } from "../shared/rules";
import { clockStore } from "../state/clock";
import { emojiOf, eventById, interestLevelOf } from "../state/feed";
import { profileStore } from "../state/profile";
import { openSheet } from "../state/view";
import { priceTagElement } from "../ui/EmojiBadge";
import { PIN_SIZE, pinFade } from "./draw";
import { drawPin } from "./paint";
import type { MapController } from "./MapController";

const TAP = SHARED.pins.tap;

/** The pin's image as an element (2x canvas, 8 px of room round it), optionally with its price label. */
function pinElement(e: FeedEvent, withPrice: boolean) {
  const now = clockStore.getState().now, level = interestLevelOf(e, profileStore.getState().view.interests);
  const style = RULES.pinStyle(e, now, { withPrice }), img = drawPin(emojiOf(e), PIN_SIZE[level], style);
  const c = document.createElement("canvas");
  c.width = img.width; c.height = img.height;
  c.getContext("2d")!.putImageData(new ImageData(img.data as Uint8ClampedArray<ArrayBuffer>, img.width, img.height), 0, 0);
  c.style.width = c.style.height = `${img.width / 2}px`;
  const wrap = document.createElement("div"), inner = document.createElement("div");
  wrap.className = "pin-marker"; inner.className = "pin-marker-inner";
  wrap.append(inner); inner.append(c);
  // Popping group pins keep their fade; the selected pin shows at full strength while you look at it.
  if (!withPrice) wrap.style.opacity = String(pinFade(e, now));
  if (style.price) {
    const tag = priceTagElement(style.price);
    tag.style.right = tag.style.bottom = "calc(8px + var(--pin-price-overhang))";
    inner.append(tag);
  }
  return wrap;
}

export class SelectedPin {
  private cur: { id: string; marker: maplibregl.Marker } | null = null;
  constructor(private readonly ctl: MapController) {}

  update(sel: FeedEvent | null) {
    if (this.cur && this.cur.id !== sel?.id) {
      const old = this.cur.marker, inner = old.getElement().firstElementChild as HTMLElement;
      inner.classList.remove("selected");
      inner.addEventListener("transitionend", () => old.remove(), { once: true });
      setTimeout(() => old.remove(), TAP.springMs + 100);
      this.cur = null;
    }
    if (!sel || this.cur) return;
    const el = pinElement(sel, true);
    el.dataset.selected = sel.id;
    el.addEventListener("click", (ev) => ev.stopPropagation());
    el.style.setProperty("--select-scale", String(TAP.selectScale));
    el.style.setProperty("--spring-ms", `${TAP.springMs}ms`);
    const marker = new maplibregl.Marker({ element: el }).setLngLat([sel.lng, sel.lat]).addTo(this.ctl.map);
    this.cur = { id: sel.id, marker };
    requestAnimationFrame(() => requestAnimationFrame(() => el.firstElementChild!.classList.add("selected")));
  }
}

let popCount = 0;
/** A group: centre on it and zoom in a step, its pins popping out from it; if zooming can't pull them apart (same
 *  venue, or zoomed right in), list them instead. */
export async function popGroup(ctl: MapController, f: maplibregl.MapGeoJSONFeature) {
  const map = ctl.map, src = map.getSource("primary") as maplibregl.GeoJSONSource;
  const leaves = await src.getClusterLeaves(Number(f.properties?.cluster_id), 200, 0);
  const ids = leaves.map((l) => String(l.properties?.id));
  const pts = leaves.map((l) => { const [lng, lat] = (l.geometry as GeoJSON.Point).coordinates as [number, number]; return { lng, lat }; });
  const spread = Math.max(0, ...pts.map((p) => RULES.metersBetween(pts[0]!, p)));
  if (spread < 150 || map.getZoom() >= 16) {
    openSheet({ kind: "results", title: T("list.manyHere", { n: leaves.length }), ids });
    return;
  }
  const center = (f.geometry as GeoJSON.Point).coordinates as [number, number];
  ctl.popping = { ids: new Set(ids), key: ++popCount };
  ctl.schedule();
  map.easeTo({ center, zoom: map.getZoom() + 1 });
  map.once("moveend", () => {
    const from = map.project(center), ms = TAP.popMs;
    const markers = ids.map(eventById).filter((e): e is FeedEvent => !!e).map((e) => {
      const to = map.project([e.lng, e.lat]), el = pinElement(e, false), inner = el.firstElementChild as HTMLElement;
      inner.style.transform = `translate(${from.x - to.x}px, ${from.y - to.y}px) scale(.4)`;
      const m = new maplibregl.Marker({ element: el }).setLngLat([e.lng, e.lat]).addTo(map);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        inner.style.transition = `transform ${ms}ms cubic-bezier(.34, 1.4, .64, 1)`;
        inner.style.transform = "";
      }));
      return m;
    });
    setTimeout(() => { ctl.popping = null; ctl.schedule(); markers.forEach((m) => m.remove()); }, ms + 30);
  });
}
